import React, { useEffect, useState } from 'react';
import { connect, MapDispatchToProps } from 'react-redux';
import { Grid, Button } from '@inplayer-org/inplayer-ui';

// actions
import { loadExternalAccount as loadExternalAccountAction } from 'ducks/merchantDucks/externalAccount/actions';

// images
import livestream from 'assets/pics/integrations/livestream-logo.png';
import laola from 'assets/pics/integrations/laola-logo.png';
import piksel from 'assets/pics/integrations/piksel.png';
import brightcove from 'assets/pics/integrations/brightcove-logo.png';
import kaltura from 'assets/pics/integrations/kaltura-logo.png';
import mediaSpace from 'assets/pics/integrations/mediaspace-logo.png';
import jwPlayer from 'assets/pics/integrations/jwplayer-logo.png';
import qBrick from 'assets/pics/integrations/qbrick-logo.png';
import ooyala from 'assets/pics/integrations/ooyala-logo.png';
import panopto from 'assets/pics/integrations/panopto-logo.png';
import dacast from 'assets/pics/integrations/dacast-logo.png';
import sportRadar from 'assets/pics/integrations/sportradar-logo.png';
import cloudfront from 'assets/pics/integrations/cloudfront-logo.png';
import wistia from 'assets/pics/integrations/wistia.png';
import wowza from 'assets/pics/integrations/wowza-logo.png';
import accedo from 'assets/pics/integrations/accedo-logo.png';
import sportOne from 'assets/pics/integrations/sportone-logo.jpg';
import streamAMG from 'assets/pics/integrations/streamamg-logo.png';
import vidyard from 'assets/pics/integrations/vidyard.png';
import flowplayer from 'assets/pics/integrations/flowplayer-logo.png';
import cloudflare from 'assets/pics/integrations/cloudflare.png';
import ivs from 'assets/pics/integrations/ivs-logo.png';

// components
import OvpCard from 'components/OvpCard';
import OvpContainer from 'components/OvpContainer';
import styled from 'styled-components';

// styled components
import { BlueTypography } from 'components/SharedStyledElements';

// constants
import { EventType } from './ContentType';

const { Container, Cell } = Grid;

export enum OvpTypes {
  ACCEDO = 'accedo',
  LIVESTREAM = 'livestream',
  BRIGHTCOVE = 'brightcove',
  KALTURA = 'kaltura',
  MEDIASPACE = 'kaltura_mediaspace',
  JWPLAYER = 'jwplayer',
  OOYALA = 'ooyala',
  QBRICK = 'qbrick',
  PANOPTO = 'panopto',
  PIKSEL = 'piksel',
  DACAST = 'dacast',
  CLOUDFRONT = 'cloudfront',
  SPORTRADAR = 'sportradar',
  SPORT1 = 'sport1',
  STREAM_AMG = 'streamamg',
  LAOLA = 'laola',
  WISTIA = 'wistia',
  WOWZA = 'wowza',
  VIDYARD = 'vidyard',
  CLOUDFLARE = 'cloudflare',
  FLOWPLAYER = 'flowplayer',
  IVS = 'ivs',
}

interface OvpButtonProps {
  key: OvpTypes;
  logo: string;
  hasQuickAdd: boolean;
  disabledFor?: Array<EventType>;
}

const StyledContainer = styled(Container)`
  margin-top: 1rem;
`;

const OvpTitle = styled(BlueTypography)`
  margin-bottom: 1rem;
`;

const ovpContent: Array<OvpButtonProps> = [
  {
    key: OvpTypes.CLOUDFRONT,
    logo: cloudfront,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.BRIGHTCOVE,
    logo: brightcove,
    hasQuickAdd: true,
  },
  {
    key: OvpTypes.DACAST,
    logo: dacast,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.JWPLAYER,
    logo: jwPlayer,
    hasQuickAdd: true,
  },
  {
    key: OvpTypes.LIVESTREAM,
    logo: livestream,
    hasQuickAdd: true,
  },
  {
    key: OvpTypes.LAOLA,
    logo: laola,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.KALTURA,
    logo: kaltura,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.MEDIASPACE,
    logo: mediaSpace,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.OOYALA,
    logo: ooyala,
    hasQuickAdd: true,
  },
  {
    key: OvpTypes.PANOPTO,
    logo: panopto,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.PIKSEL,
    logo: piksel,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.QBRICK,
    logo: qBrick,
    hasQuickAdd: true,
  },
  {
    key: OvpTypes.SPORTRADAR,
    logo: sportRadar,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.WISTIA,
    logo: wistia,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.WOWZA,
    logo: wowza,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.ACCEDO,
    logo: accedo,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.SPORT1,
    logo: sportOne,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.STREAM_AMG,
    logo: streamAMG,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.VIDYARD,
    logo: vidyard,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.CLOUDFLARE,
    logo: cloudflare,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.FLOWPLAYER,
    logo: flowplayer,
    hasQuickAdd: false,
  },
  {
    key: OvpTypes.IVS,
    logo: ivs,
    hasQuickAdd: false,
    disabledFor: Object.values(EventType).filter((eventType) => eventType !== EventType.LIVE),
  },
];

interface OwnProps {
  onOvpSelected: (ovpType: string) => void;
  onCompleteSelection: (buttonType: string) => () => any;
  onBackButtonClick: () => void;
  eventType: EventType;
}

interface DispatchProps {
  loadExternalAccount: (ovpName: string) => any;
}

type Props = OwnProps & DispatchProps;

const OvpVideoType = (props: Props) => {
  const { onOvpSelected, onCompleteSelection, loadExternalAccount, onBackButtonClick, eventType } = props;

  const [selectedOvpType, setOvpType] = useState('');
  const [buttonType, setButtonType] = useState('');

  useEffect(() => {
    ovpContent.forEach((ovp) => {
      if (ovp.hasQuickAdd) {
        loadExternalAccount(ovp.key);
      }
    });
  }, []);

  const handleOvpSelect = (ovpType: string, butonType: string) => {
    onOvpSelected(ovpType!);
    setOvpType(ovpType!);
    setButtonType(butonType!);
  };

  const renderOvpCards = () => {
    return ovpContent
      .filter(({ disabledFor }) => !disabledFor?.includes(eventType))
      .map(({ key: ovpKey, logo, hasQuickAdd }) => {
        return (
          <OvpCard
            tag={ovpKey}
            assetSection
            logo={logo}
            key={ovpKey}
            ovpType={ovpKey}
            hasQuickAdd={hasQuickAdd}
            isSelected={selectedOvpType === ovpKey}
            handleOvpSelect={handleOvpSelect}
          />
        );
      });
  };

  return (
    <>
      <OvpTitle variant="h5">Select your Online Video Platform</OvpTitle>
      <OvpContainer assetSection>{renderOvpCards()}</OvpContainer>
      <StyledContainer columns={3}>
        <Cell>
          <Button tag="button_back" fullWidth onClick={onBackButtonClick} buttonModifiers={['buttonLink']}>
            Back
          </Button>
        </Cell>
        <Cell left={3} top={1}>
          <Button
            tag="button_next"
            fullWidth
            disabled={selectedOvpType === ''}
            onClick={onCompleteSelection(buttonType)}
            type="submit"
            buttonModifiers={['buttonPrimary']}
          >
            Next
          </Button>
        </Cell>
      </StyledContainer>
    </>
  );
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, {}> = {
  loadExternalAccount: loadExternalAccountAction,
};

export default connect(null, mapDispatchToProps)(OvpVideoType);
